/**
 * Badge Tooltip Component
 * Shows badge details on hover/focus
 */

import { type ReactNode, useState } from 'react';
import { cn } from '../../lib/utils';

interface BadgeTooltipProps {
  children: ReactNode;
  badgeName: string;
  description: string;
  earnedAt?: string; // ISO string (optional)
  className?: string;
}

function formatEarnedDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Earned today';
  if (diffInDays === 1) return 'Earned yesterday';
  if (diffInDays < 7) return `Earned ${diffInDays} days ago`;
  if (diffInDays < 30) return `Earned ${Math.floor(diffInDays / 7)} weeks ago`;

  return `Earned ${date.toLocaleDateString()}`;
}

export function BadgeTooltip({
  children,
  badgeName,
  description,
  earnedAt,
  className = '',
}: BadgeTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const handleMouseEnter = (event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 280; // Approximate tooltip width
    const viewportWidth = window.innerWidth;

    // Calculate position
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;

    // Adjust if tooltip would go off-screen
    if (left < 10) left = 10;
    if (left + tooltipWidth > viewportWidth - 10) {
      left = viewportWidth - tooltipWidth - 10;
    }

    setPosition({
      top: rect.top - 10, // Position above the element
      left,
    });
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  const handleFocus = (event: React.FocusEvent) => {
    // Create a mock mouse event for consistency
    const mockMouseEvent = {
      currentTarget: event.currentTarget,
    } as React.MouseEvent;
    handleMouseEnter(mockMouseEvent);
  };

  const handleBlur = () => {
    setIsVisible(false);
  };

  return (
    <>
      <div
        className={cn('relative', className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        tabIndex={0}
        role="button"
        aria-describedby={isVisible ? 'badge-tooltip' : undefined}
      >
        {children}
      </div>

      {/* Tooltip portal */}
      {isVisible && (
        <div
          id="badge-tooltip"
          role="tooltip"
          className="fixed z-50 w-70 p-3 bg-gray-900 dark:bg-gray-800 text-white text-sm rounded-lg shadow-lg border border-gray-600"
          style={{
            top: position.top,
            left: position.left,
            transform: 'translateY(-100%)',
          }}
        >
          {/* Arrow */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2">
            <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-800" />
          </div>

          {/* Content */}
          <div className="space-y-2">
            <h4 className="font-semibold text-white">{badgeName}</h4>
            <p className="text-gray-300 text-xs leading-relaxed">
              {description}
            </p>
            {earnedAt && (
              <p className="text-gray-400 text-xs">
                {formatEarnedDate(earnedAt)}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
